import IndianCommunityHeadtBeatImg from "@assets/HeartBeatIndianCommunity.svg";
import shareIcon from "@assets/social_media/greyShareIcon.svg";
import ArticleCard from "@components/Cards/ArticleCard";
import EpaperBanner from "@elements/Banner/Epaper/EpaperBanner";
import Image from "@elements/ResponsiveImage/Image";
import ShareSocialButton from "@elements/ShareSocial/ShareSocialButton";
import Date from "@elements/Typography/Date";
import Heading from "@elements/Typography/Heading";
import { colorConstants } from "@helper/sectionConstants";
import { formatDate } from "@helper/useDateFormat";
import { NoSSR } from "@sphtech/web2-core/components";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { useEffect, useState } from "react";
import { Link } from "react-router";

export const TopTablaNewsSection = ({
  topNews,
  onScollTitleAnimation = false,
  epaperCoverImageUrl,
}: {
  topNews: ProcessedArticleData[];
  onScollTitleAnimation?: boolean;
  epaperCoverImageUrl?: string;
}) => {
  const source = "home-top-stories";
  const remainingArticles = topNews.slice(1);
  const firstNewsArticle = topNews[0];
  const firstArticleSectionName = firstNewsArticle.sectionName;
  const sectionColorData =
    firstArticleSectionName &&
    firstArticleSectionName in colorConstants &&
    colorConstants[firstArticleSectionName as keyof typeof colorConstants];

  const firstArticleColorConst = sectionColorData
    ? sectionColorData.color.darkBg !== ""
      ? sectionColorData.color.darkBg
      : sectionColorData.color.lightBg
    : "";

  const socialIconList = ["whatsapp", "facebook", "copylink", "x"];

  const [mode, setMode] = useState<"center" | "stuck">("center");

  useEffect(() => {
    const onScroll = () => {
      const image = document.querySelector(".main-image") as HTMLElement;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!image && onScollTitleAnimation) return;

      const rect = image.getBoundingClientRect();
      const middleY = rect.top + rect.height / 2;

      if (middleY <= window.innerHeight / 3) {
        setMode("stuck"); // title sticks under image
      } else {
        setMode("center"); // title floats in middle of image
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [onScollTitleAnimation]);

  return (
    <>
      {/* Desktop screen */}
      <div className="bg-grey-200 hidden lg:flex xmd:pb-xxl lg:pb-xxs">
        <div className="md:max-w-screen-xmd xmd:max-w-screen-lg lg:max-w-7xl lg:w-full lg:px-2.5 xmd:flex m-auto xmd:self-stretch items-start gap-3">
          <div className="lg:min-w-[260px] lg:max-w-[305px] lg:w-full xl:w-76">
            <div className="flex flex-col gap-5 overflow-hidden">
              <div className="flex-col">
                <ArticleCard
                  displayWidth={632}
                  displayHeight={439}
                  {...remainingArticles[0]}
                  elements={{
                    hasParagraph: false,
                    hasDate: true,
                    hasSectionName: true,
                    hasDarkBg: true,
                  }}
                  overrideStyle={{
                    cardWrapper:
                      "w-full xmd:w-fit flex flex-col xmd:border-b-0 xmd:py-0 lg:gap-0 xmd:gap-0 lg:gap-2",
                    titleClass:
                      "font-bold font-primary leading-8 text-2xl hover:no-underline text-grey-400",
                    imageWrapperStyle: "w-full max-w-full",
                    titleWrapper: "gap-0",
                    imageStyle: "h-55",
                    dateClass: "text-grey-300 font-opensans text-sm",
                    contentWrapper: "!gap-3 lg:!gap-1",
                  }}
                  shareIcon={shareIcon}
                  refMedium={source}
                />
              </div>
              <div className="flex flex-col leading-6 gap-5">
                {remainingArticles
                  .filter((_, i) => i > 0)
                  .map((article) => (
                    <ArticleCard
                      key={article.id}
                      displayWidth={110}
                      displayHeight={80}
                      {...article}
                      elements={{
                        hasParagraph: false,
                        hasDate: true,
                        hasSectionName: true,
                        hasDarkBg: true,
                      }}
                      overrideStyle={{
                        cardWrapper:
                          "w-full lg:w-full !py-0 xmd:flex-col lg:flex-row !gap-2",
                        titleClass:
                          "font-bold font-opensans text-sm leading-5 text-grey-400 hover:text-grey-400 hover:no-underline",
                        imageWrapperStyle: "xmd:max-w-full lg:w-23 xl:w-27",
                        titleWrapper: "gap-0",
                        imageStyle: "w-27",
                        dateClass: "text-grey-300 font-opensans text-sm",
                        contentWrapper: "px-2 gap-2",
                      }}
                      shareIcon={shareIcon}
                      refMedium={source}
                    />
                  ))}
              </div>
            </div>
          </div>
          <div className="lg:min-w-[456px] lg:max-w-[623px] lg:w-full xl:w-160 gap-3">
            <ArticleCard
              displayWidth={976}
              displayHeight={732}
              {...firstNewsArticle}
              overrideStyle={{
                cardWrapper:
                  "w-full lg:w-fit flex flex-col lg:border-b-0 xmd:py-0 xmd:gap-6",
                paragraphStyle:
                  "text-xl text-grey-400 font-normal font-opensans leading-7.5",
                titleClass:
                  "font-bold font-primary text-5xl hover:no-underline leading-12",
                titleStyle: {
                  color: firstArticleColorConst,
                },
                imageWrapperStyle: "w-full max-w-full main-image",
                titleWrapper: "gap-0",
                imageStyle: "h-80 xmd:h-140",
                dateClass: "text-grey-300 font-opensans text-sm leading-5",
                contentWrapper:
                  onScollTitleAnimation && mode === "center"
                    ? "sticky bottom-0 bg-grey-200 pt-5 pb-3 w-auto"
                    : onScollTitleAnimation
                      ? "hidden"
                      : "relative pr-10",
              }}
              elements={{
                hasParagraph: true,
                hasTruncated: true,
                hasDate: true,
                hasSectionName: true,
                hasDarkBg: true,
              }}
              shareIcon={shareIcon}
              refMedium={source}
              fetchPriority="high"
            />
            {onScollTitleAnimation && (
              <div className="flex flex-col w-full gap-xs">
                {firstNewsArticle.sectionName && (
                  <div
                    className="w-max p-1 font-bold leading-4.5 text-xs font-opensans text-grey-400"
                    style={{
                      borderTop: `3px solid firstArticleColorConst
                      }`,
                    }}
                  >
                    {firstNewsArticle.sectionName}
                  </div>
                )}
                <div className="flex flex-col gap-0">
                  <Link
                    reloadDocument
                    to={`${firstNewsArticle.linkTo}?ref=${source}`}
                  >
                    <Heading
                      Element="h1"
                      className="font-bold font-primary text-5xl leading-12 hover:no-underline text-red-100"
                      style={{
                        color: firstArticleColorConst,
                      }}
                    >
                      {firstNewsArticle.title}
                    </Heading>
                  </Link>
                </div>
                <p className="font-regular lg:text-2sm leading-300 text-base text-grey-400 font-normal font-opensans leading-6">
                  {firstNewsArticle.paragraph}
                </p>
                <div className="inline-flex justify-between">
                  {firstNewsArticle.createdDate && (
                    <NoSSR>
                      <Date className="text-grey-300">
                        {formatDate(
                          firstNewsArticle.createdDate,
                          "d MMM y H:mm",
                        )}
                      </Date>
                    </NoSSR>
                  )}
                  <ShareSocialButton
                    socialIconList={socialIconList}
                    pagePath={firstNewsArticle.linkTo}
                    shareIcon={shareIcon}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="block px-10 xmd:px-xxs lg:px-5">
            <EpaperBanner epaperCoverImageUrl={epaperCoverImageUrl} />
            <Image
              objectFit="initial"
              src={IndianCommunityHeadtBeatImg}
              className="mx-auto max-w-[320px] md:min-w-[198px] xmd:max-w-[230px] lg:min-w-[230px] lg:max-w-[270px] lg:w-full"
              srcWidth={266}
              srcHeight={355}
              alt="newsletter-logo"
              dataTestId="image"
            />
          </div>
        </div>
      </div>
      {/* Mobile screen */}
      <div className="flex lg:hidden flex-col md:gap-5 bg-grey-200 pb-7 px-4 md:px-4 md:pb-5">
        <div className="max-w-full flex">
          <ArticleCard
            displayWidth={495}
            displayHeight={474}
            {...firstNewsArticle}
            elements={{
              hasParagraph: true,
              hasTruncated: true,
              hasDate: true,
              hasSectionName: true,
              hasDarkBg: true,
            }}
            overrideStyle={{
              cardWrapper: "w-full flex flex-col xmd:py-0 mb-4 md:mb-0",
              paragraphStyle:
                "text-base text-grey-400 font-normal font-opensans mb-3 md:mb-5",
              titleClass:
                "font-bold font-primary text-100 hover:no-underline leading-11 mt-3 md:mt-1 mb-xxsm md:mb-3",
              titleStyle: {
                color: firstArticleColorConst,
              },
              imageWrapperStyle:
                "w-full max-w-full xmd:min-w-[495px] h-73 md:h-80",
              imageStyle: "h-73 md:h-80",
              titleWrapper: "gap-0",
              contentWrapper: "md:pl-0 gap-0",
              dateClass: "text-grey-300",
            }}
            shareIcon={shareIcon}
            refMedium={source}
            fetchPriority="high"
          />
          <EpaperBanner
            epaperCoverImageUrl={epaperCoverImageUrl}
            className="hidden md:block px-10 xmd:px-xxs lg:px-5"
          />
        </div>
        <div className="flex-col md:flex-row flex gap-4 overflow-hidden">
          {topNews
            .filter((_, i) => i > 0)
            .map((article) => (
              <ArticleCard
                key={article.id}
                displayWidth={248}
                displayHeight={186}
                {...article}
                elements={{
                  hasParagraph: false,
                  hasDate: true,
                  hasSectionName: true,
                  hasDarkBg: true,
                }}
                overrideStyle={{
                  cardWrapper:
                    "w-full lg:w-full !py-0 md:flex-col gap-3 md:gap-3",
                  titleClass:
                    "font-bold font-opensans text-sm leading-5 text-grey-400 hover:text-grey-400 hover:no-underline",
                  imageWrapperStyle: "max-w-[140px]",
                  contentWrapper: "gap-0 xmd:gap-2",
                  sectionNameClass: "pb-3 md:pb-0",
                  titleWrapper: "gap-0 pb-1.6 md:pb-0",
                  imageStyle: "xmd:min-w-[191px] md:w-full",
                  dateClass: "text-grey-300 font-opensans text-sm",
                  bottomInfoWrapperStyle: "mt-auto",
                }}
                shareIcon={shareIcon}
                refMedium={source}
              />
            ))}
          <EpaperBanner
            epaperCoverImageUrl={epaperCoverImageUrl}
            className="block px-10 xmd:px-xxs lg:px-5 md:hidden max-w-[390px] mx-auto mt-6"
          />
        </div>
      </div>
    </>
  );
};
